'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Phone, PhoneCall, MicOff, WifiOff, Loader2 } from 'lucide-react';
import {
  useIncomingCallUiModeContext,
  useWebphoneContext,
} from '@/hooks/voice/webphone-provider';
import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS_UPDATED_EVENT } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type { ActiveCall, WebphoneState } from '@/hooks/voice/webphone-types';
import { WebphonePanelIdle } from './webphone-panel-idle';
import { WebphonePanelActive } from './webphone-panel-active';
import { IncomingCallModal } from './incoming-call-modal';
import {
  IncomingCallCard,
  INCOMING_CARD_WIDTH_PX,
  INCOMING_CARD_HEIGHT_PX,
  INCOMING_CARD_MINIMIZED_WIDTH_PX,
  INCOMING_CARD_MINIMIZED_HEIGHT_PX,
} from './incoming-call-card';
import { clampToViewport, useDragPosition } from './use-drag-position';
import { WebphoneTransferDialog } from './webphone-transfer-dialog';
import { reduceActiveCall } from './active-call-reducer';
import { useRingtone } from './use-ringtone';
import { useVoiceSocket } from '@/hooks/use-voice-socket';
import { toDialableBr } from '@/hooks/voice/phone-format';
import { useIncomingCallNotification } from '@/hooks/voice/use-incoming-call-notification';
import { toast } from 'sonner';

// Persistência da posição arrastada (pixels do canto bottom-right). Sobrevive
// reload. Reset: localStorage.removeItem('gk-webphone-pos').
const POS_STORAGE_KEY = 'gk-webphone-pos';
const DEFAULT_POS = { bottom: 24, right: 24 }; // bottom-6 right-6 em px
const BUBBLE_SIZE = 56; // h-14 w-14

// #441: as medidas do CARTAO da chamada entrante moram no proprio cartao — ele
// e quem sabe o tamanho que tem. Re-exportadas daqui porque quem afere o limite
// de viewport e o teste DESTE arquivo (o de quem posiciona), e ele importa
// estes nomes deste modulo desde a #441.
export {
  INCOMING_CARD_WIDTH_PX,
  INCOMING_CARD_HEIGHT_PX,
  INCOMING_CARD_MINIMIZED_WIDTH_PX,
  INCOMING_CARD_MINIMIZED_HEIGHT_PX,
};

// Medidas do PAINEL aberto. Mesma historia da #441, no componente que ela nao
// alcancou: o painel tambem e ancorado pela borda de BAIXO na coordenada da
// bolha, e tambem e muito maior que ela — com a bolha arrastada pro alto, abrir
// o painel jogava o teclado e o botao de Ligar pra fora da tela.
//
// Largura: o `w-80` dos dois paineis — conferido, bate exato.
//
// Altura: MEDIDA, nao estimada. A revisao adversarial replicou o DOM dos dois
// paineis com as classes reais, compilou o Tailwind do projeto e mediu no
// Chromium:
//
//   idle registrado, sem erro ......... 529 px
//   idle com bloco de erro (no_creds) . 636 px
//   ativo, em chamada, ficha completa . 659 px
//   ativo + teclado DTMF aberto ....... 868 px
//
// ⚠️ NENHUMA constante esta certa, e por isso o valor NAO foi mexido: a altura
// varia 339 px entre os estados, entao qualquer numero fixo erra na maioria
// deles — so muda pra que lado.
//
// Subir pra 680 (cobrindo `ativo`) foi TENTADO e revertido. O clamp e o TETO do
// `bottom`, entao valor maior significa MENOS alcance de arrasto, nao mais
// protecao. Numa viewport de 800 px o painel idle de 529 px pode legitimamente
// ir ate `bottom: 271`; com 680 o teto cai pra 120 e somem 151 px de alcance
// REAL, todo dia, pra proteger um estado transitorio. Quebrou o teste "o painel
// e arrastavel pelo cabecalho, e a posicao fica gravada", que estava CERTO.
//
// Manter 620 tambem nao esta certo: super-restringe o idle em 91 px, sub-protege
// `ativo` em 39 px e o DTMF aberto em 248 px — nesse extremo a alca e o botao de
// minimizar saem por cima e o agente nao traz o painel de volta durante a
// chamada.
//
// A saida e MEDIR o elemento real e usar a MESMA medida no clamp do render e no
// do arrasto; a objecao de que as duas divergiriam so vale se cada uma medir por
// conta. Registrado como issue em vez de remendado no fim de uma entrega.
export const PANEL_WIDTH_PX = 320; // w-80 — conferido, bate exato
export const PANEL_HEIGHT_PX = 620;

function loadStoredPos(): { bottom: number; right: number } {
  if (typeof window === 'undefined') return DEFAULT_POS;
  try {
    const raw = localStorage.getItem(POS_STORAGE_KEY);
    if (!raw) return DEFAULT_POS;
    const parsed = JSON.parse(raw) as { bottom?: number; right?: number };
    if (typeof parsed.bottom !== 'number' || typeof parsed.right !== 'number') {
      return DEFAULT_POS;
    }
    return parsed as { bottom: number; right: number };
  } catch {
    return DEFAULT_POS;
  }
}

/**
 * Grava o canto escolhido pra BOLHA/PAINEL. Chamado ao SOLTAR o arrasto, nao a
 * cada movimento — o cartao da chamada entrante, de proposito, nao chama isto
 * (ver `IncomingCallDock`).
 */
function persistPos(p: { bottom: number; right: number }): void {
  try {
    localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(p));
  } catch {
    // localStorage cheio/desativado — perde só a persistência, não trava UI
  }
}

export function WebphoneSticky() {
  const wp = useWebphoneContext();
  // #402: lido UMA vez por aba, no provider. Aqui é só leitura de contexto —
  // o hook tem que ficar acima dos returns condicionais (regra dos hooks).
  const incomingCallUiMode = useIncomingCallUiModeContext();
  const [expanded, setExpanded] = useState(false);
  const [hasPerms, setHasPerms] = useState(false);
  // #112: gate do socket /voice — alinhado a permissao que o VoiceGateway exige
  // (telefonia.dashboard.ver). Sem isso o webphone abria /voice em TODA aba de
  // qualquer usuario logado num tenant com telefonia ligada (storm user=undefined).
  const [canConnectVoice, setCanConnectVoice] = useState(false);
  const [pos, setPos] = useState<{ bottom: number; right: number }>(DEFAULT_POS);
  const [agentName, setAgentName] = useState<string | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  // unique_id (motor) da chamada ATIVA deste agente. Antes era adivinhado
  // casando o ramal contra a lista de chamadas ativas — DAVA 404 quando o
  // CDR ainda não tinha esse ramal preenchido. Contrato DEFINITIVO do motor
  // (2026-06-13): o uid vem do evento ANCHOR `voice.bridged`. Fonte única.
  // uid (motor) + protocolo (display_id da conversa) da chamada ATIVA deste agente,
  // num objeto só pra o clear no hangup ser atômico (o callback do socket tem deps
  // [], então ler state solto seria stale). Fonte única: evento ANCHOR voice.bridged
  // (inbound) / voice.ringing (outbound). O protocolo o agente vê durante a ligação.
  const [activeCall, setActiveCall] = useState<{ uid: string; protocolo: string | null } | null>(null);
  const activeUniqueId = activeCall?.uid ?? null;
  // wp.extension muda de render; ref evita re-subscrever o socket a cada toggle.
  const extensionRef = useRef<string | null>(wp.extension);
  extensionRef.current = wp.extension;
  // Ref do webphone pro listener global (click-to-call) ler sempre o estado
  // atual sem re-registrar o event listener a cada render.
  const wpRef = useRef(wp);
  wpRef.current = wp;

  // Ringback de saida: o browser nao gera o "puuu...." sozinho ao receber o 180
  // Ringing (softphone/telefone IP geram nativamente). Toca enquanto a chamada de
  // SAIDA esta discando/chamando e ainda nao foi atendida. Hook antes dos returns
  // condicionais abaixo (regra dos hooks). Chamada RECEBIDA usa o ringtone do
  // incoming-call-modal, entao aqui filtramos por direction outbound.
  const isOutboundRinging =
    wp.call?.direction === 'outbound' &&
    (wp.state === 'calling' || wp.state === 'ringing');
  useRingtone(isOutboundRinging, 'ringback');

  const { connected: voiceConnected } = useVoiceSocket(
    useCallback((evt) => {
      // #407: a decisão de ancorar (ou não) vive em `reduceActiveCall`, função
      // pura e testada. Estava aqui dentro, inline e sem teste, e foi
      // exatamente onde o defeito se escondeu: o ramo outbound aceitava a
      // discagem de QUALQUER ramal do tenant.
      //
      // O reducer devolve a mesma referência quando o evento não é deste
      // agente, então o setState não re-renderiza a cada ligação de colega.
      setActiveCall((prev) => reduceActiveCall(prev, evt, extensionRef.current));
    }, []),
    // #112: permissao (telefonia.dashboard.ver) E #178: socket só abre com o
    // SIP de pé — creds OK implica ramal vinculado. Agente sem ramal (ou motor
    // fora) não gera nem 1 handshake /voice pelo sticky; os eventos que este
    // socket consome são da PRÓPRIA chamada, impossível sem registro SIP.
    canConnectVoice &&
      (wp.state === 'registered' ||
        wp.state === 'calling' ||
        wp.state === 'ringing' ||
        wp.state === 'in_call'),
  );

  // Recovery: se o socket /voice caiu e reconectou DURANTE uma chamada, o evento
  // ANCHOR `voice.bridged` pode ter se perdido na janela — fica sem activeUniqueId
  // e o botão Transferir trava cinza mesmo com o agente em ligação. Ao reconectar
  // (e só se ainda estou em chamada sem uid), re-descubro a chamada ativa deste
  // ramal via GET /telefonia/chamadas/ativa. Falha silenciosa: o recovery é
  // best-effort, os eventos do socket continuam sendo a fonte primária.
  useEffect(() => {
    if (!voiceConnected || activeUniqueId || wp.state !== 'in_call') return;
    api
      .request<{
        chamada: {
          unique_id: string;
          answered_by_extension: string;
          protocolo: string | null;
        } | null;
      }>('/telefonia/chamadas/ativa')
      .then(({ chamada }) => {
        if (chamada && chamada.answered_by_extension === extensionRef.current) {
          setActiveCall({ uid: chamada.unique_id, protocolo: chamada.protocolo ?? null });
        }
      })
      .catch(() => {});
  }, [voiceConnected, activeUniqueId, wp.state]);

  useEffect(() => {
    // #112 hardening: reavalia as permissões no mount E quando elas chegam/mudam
    // (boot async, /auth/permissions repovoando o storage, admin concedendo
    // telefonia.* na sessão, ou outra aba renovando). Sem isto, um telefonista que
    // só recebe a permissão DEPOIS do mount ficaria sem webphone até dar F5.
    const applyPerms = () => {
      setHasPerms(hasPermission('telefonia.chamadas.originar'));
      setCanConnectVoice(hasPermission('telefonia.dashboard.ver')); // #112
    };
    applyPerms();
    setPos(clampToViewport(loadStoredPos(), BUBBLE_SIZE, BUBBLE_SIZE));
    setAgentName(api.getUser()?.name ?? null);
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, applyPerms);
    window.addEventListener('storage', applyPerms);
    return () => {
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, applyPerms);
      window.removeEventListener('storage', applyPerms);
    };
  }, []);

  // Auto-connect quando permissao presente
  useEffect(() => {
    if (hasPerms && wp.state === 'disconnected') {
      void wp.connect();
    }
  }, [hasPerms, wp]);

  // Click-to-call: outras telas (ex: ficha do cliente no Cockpit) disparam um
  // CustomEvent `gk:voice-dial` com o número. O webphone — única instância que
  // mantém o registro SIP — escuta e disca. Sem acoplar essas telas ao hook.
  useEffect(() => {
    function handleVoiceDial(e: Event) {
      // Normaliza pro formato da rota de saída (0 + DDD + número) — o ERP manda
      // em formatos mistos (com/sem 55, com máscara). Ver toDialableBr.
      const number = toDialableBr(
        (e as CustomEvent<{ number?: string }>).detail?.number ?? '',
      );
      if (!number) return;
      const w = wpRef.current;
      if (w.state !== 'registered') {
        toast.error(
          'Webphone não está registrado. Abra o webphone e aguarde conectar para ligar.',
        );
        setExpanded(true);
        return;
      }
      setExpanded(true);
      void w.dial(number);
    }
    window.addEventListener('gk:voice-dial', handleVoiceDial as EventListener);
    return () =>
      window.removeEventListener('gk:voice-dial', handleVoiceDial as EventListener);
  }, []);

  // #435 — aviso nativo do sistema quando a ligacao entra com esta aba em
  // segundo plano. Vive AQUI, acima dos early-returns (regra dos hooks), e nao
  // dentro do `use-webphone.ts`, que nao tem arquivo de teste.
  //
  // `wpRef.current` em vez de `wp` nos callbacks: o listener do SW e registrado
  // uma vez so e le sempre o estado atual. As duas linhas de `onAccept` sao as
  // mesmas do botao Atender do modal/card — repetidas de proposito, pra nao
  // reescrever o branch de render da chamada entrante (territorio da #402).
  const callNotification = useIncomingCallNotification({
    state: wp.state,
    call: wp.call,
    onAccept: () => {
      wpRef.current.acceptIncoming();
      setExpanded(true);
    },
    onReject: () => wpRef.current.rejectIncoming(),
  });

  // Arrasto do PAINEL aberto (a bolha ja arrastava; o painel, nao — clicar nela
  // trocava a bolha pelo painel no mesmo container, sem alca nenhuma).
  //
  // Hook acima dos early returns (regra dos hooks). A posicao vai pro MESMO
  // `gk-webphone-pos` da bolha, de proposito: painel e bolha sao o mesmo objeto
  // em dois estados — arrastar o painel e mover o webphone, e a bolha tem que
  // aparecer onde ele foi largado. Isso e o OPOSTO do cartao da chamada
  // entrante, que e transitorio (~30s) e nao pode roubar o canto escolhido.
  const panelPos = clampToViewport(pos, PANEL_WIDTH_PX, PANEL_HEIGHT_PX);
  const panelDrag = useDragPosition({
    pos: panelPos,
    size: { width: PANEL_WIDTH_PX, height: PANEL_HEIGHT_PX },
    onChange: setPos,
    onCommit: persistPos,
  });

  const handleToggleNotification = () => {
    // Ja concedida: o clique so liga/desliga a preferencia deste device.
    // Ainda nao concedida: ESTE clique e o gesto que autoriza pedir.
    if (callNotification.permission === 'granted') {
      callNotification.setEnabled(!callNotification.enabled);
      return;
    }
    void callNotification.requestPermission();
  };

  if (!hasPerms) return null;
  // #178: agente sem ramal vinculado — nem todo agente atende telefone; o
  // sticky some por completo (decisão de produto 10/07). Admin vinculou
  // depois? O próximo page-load re-tenta (o probe é o próprio connect).
  if (wp.error?.code === 'no_extension') return null;

  const isRinging = wp.state === 'ringing';
  const isIncoming = isRinging && wp.call?.direction === 'inbound';
  // outbound ringing tambem mostra panel de chamada (modal so para inbound)
  const inCall =
    wp.state === 'in_call' ||
    wp.state === 'calling' ||
    (isRinging && !isIncoming);

  // Chamada inbound tocando: um dos dois modos, NUNCA os dois (#402). Ambos
  // chamam useRingtone(true) por dentro — montar os dois seria dois
  // AudioContext tocando ao mesmo tempo.
  if (isIncoming && wp.call) {
    // Modo DISCRETO (o padrão desde 26/08/2026): o cartão nasce no canto onde a
    // bolha está (a posição arrastada, `gk-webphone-pos`) e SUBSTITUI a bolha
    // enquanto toca — senão os dois se empilham no mesmo canto. Daí em diante
    // ele anda por conta própria, sem mexer no canto guardado.
    if (incomingCallUiMode === 'discreet') {
      return (
        <IncomingCallDock
          // A chamada acaba, e o proximo toque tem que comecar do zero: cartao
          // inteiro, no canto da bolha. Trocar a `key` a cada ligacao TROCA a
          // instancia — o "minimizado" e o "arrastado" desta ligacao morrem com
          // ela, sem nenhum reset manual pra alguem esquecer de chamar. Um
          // cartao que lembrasse do minimizado nasceria escondido na ligacao
          // seguinte, que e pior que o problema que minimizar resolve.
          key={wp.call.id}
          call={wp.call}
          basePos={pos}
          onAccept={() => {
            wp.acceptIncoming();
            setExpanded(true);
          }}
          onReject={() => wp.rejectIncoming()}
        />
      );
    }
    // Modo TELA CHEIA: byte-idêntico ao que sempre foi — só deixou de ser o
    // padrão (26/08/2026). Quem escolheu `fullscreen` na tela do canal, ou já
    // tinha esse valor gravado, continua vendo exatamente isto.
    return (
      <IncomingCallModal
        call={wp.call}
        onAccept={() => {
          wp.acceptIncoming();
          setExpanded(true);
        }}
        onReject={() => wp.rejectIncoming()}
      />
    );
  }

  // #441 no painel: com o painel ABERTO o limite e o dele, nao o da bolha (ver
  // PANEL_HEIGHT_PX). Só no render — `pos` e o `localStorage` continuam com o
  // canto que o agente escolheu; fechar o painel devolve a bolha pra la.
  const containerPos = expanded ? panelPos : pos;

  return (
    <div
      className={cn(
        'fixed z-40',
        // Transicao de posicao durante o arrasto faz o painel "nadar" atras do
        // cursor; desligada enquanto o ponteiro esta segurando.
        panelDrag.dragging ? 'transition-none' : 'transition-all duration-200 ease-out',
      )}
      style={{ bottom: `${containerPos.bottom}px`, right: `${containerPos.right}px` }}
    >
      {expanded ? (
        inCall && wp.call ? (
          <>
            <WebphonePanelActive
              call={wp.call}
              state={wp.state}
              protocolo={activeCall?.protocolo ?? null}
              onDragHandlePointerDown={panelDrag.onPointerDown}
              onMinimize={() => setExpanded(false)}
              onHangup={wp.hangup}
              onToggleMute={wp.toggleMute}
              onToggleHold={wp.toggleHold}
              onSendDtmf={wp.sendDtmf}
              onTransfer={() => setTransferOpen(true)}
            />
            <WebphoneTransferDialog
              open={transferOpen}
              onOpenChange={setTransferOpen}
              callUid={activeUniqueId}
              ownExtension={wp.extension}
              onTransferred={() => {
                // Blind transfer larga a chamada na hora — encerra estado local.
                setActiveCall(null);
                wp.hangup();
                setExpanded(false);
              }}
            />
          </>
        ) : (
          <WebphonePanelIdle
            state={wp.state}
            error={wp.error}
            extension={wp.extension}
            agentName={agentName}
            onDragHandlePointerDown={panelDrag.onPointerDown}
            onMinimize={() => setExpanded(false)}
            onDial={(num) => void wp.dial(num)}
            onSimulateIncoming={wp.simulateIncoming}
            onReconnect={() => void wp.connect()}
            notificationState={callNotification.uiState}
            onToggleNotification={handleToggleNotification}
          />
        )
      ) : (
        <WebphoneBubble
          state={wp.state}
          inCall={inCall}
          muted={wp.call?.muted ?? false}
          pos={pos}
          onClick={() => setExpanded(true)}
          onPosChange={setPos}
          onCommitPos={persistPos}
        />
      )}
    </div>
  );
}

interface DockProps {
  call: ActiveCall;
  /** Canto onde a BOLHA esta — de onde o cartao nasce. */
  basePos: { bottom: number; right: number };
  onAccept: () => void;
  onReject: () => void;
}

/**
 * Container posicionado do cartao da chamada entrante (modo discreto).
 *
 * Existe como componente separado por causa do CICLO DE VIDA: ele so monta
 * enquanto ha chamada tocando, entao "minimizado" e "arrastado pra ca" morrem
 * junto com a ligacao — sem reset manual, que e o tipo de coisa que se esquece
 * de chamar num dos tres caminhos de fim de chamada (atendeu, recusou,
 * desistiram). O `key` no call site amarra a instancia a ligacao.
 *
 * A posicao arrastada NAO e persistida em lugar nenhum, e nao toca no
 * `gk-webphone-pos` (#441): o agente arrastou a BOLHA pro canto dela por um
 * motivo, e este cartao dura ~30s. Empurrar o cartao pro lado pra ler algo
 * atras dele nao pode mudar, em silencio, onde o webphone mora.
 */
function IncomingCallDock({ call, basePos, onAccept, onReject }: DockProps) {
  const [minimized, setMinimized] = useState(false);
  // `null` = "segue a bolha". Vira coordenada propria no primeiro arrasto.
  const [dragPos, setDragPos] = useState<{ bottom: number; right: number } | null>(
    null,
  );

  // As medidas mudam com o estado — e o limite de viewport muda junto. Prender
  // aqui, no render, e o que cobre os dois lados: a posicao herdada da bolha
  // (#441) e a posicao arrastada quando o cartao volta a crescer (arrastar
  // minimizado pro alto e depois expandir devolveria o cartao cortado).
  const size = minimized
    ? {
        width: INCOMING_CARD_MINIMIZED_WIDTH_PX,
        height: INCOMING_CARD_MINIMIZED_HEIGHT_PX,
      }
    : { width: INCOMING_CARD_WIDTH_PX, height: INCOMING_CARD_HEIGHT_PX };
  const shown = clampToViewport(dragPos ?? basePos, size.width, size.height);

  const drag = useDragPosition({
    pos: shown,
    size,
    onChange: setDragPos,
    // Sem `onCommit`: nada de persistir — ver o docblock.
  });

  return (
    <div
      className={cn(
        'fixed z-40',
        drag.dragging ? 'transition-none' : 'transition-all duration-200 ease-out',
      )}
      style={{ bottom: `${shown.bottom}px`, right: `${shown.right}px` }}
    >
      <IncomingCallCard
        call={call}
        onAccept={onAccept}
        onReject={onReject}
        minimized={minimized}
        onToggleMinimize={() => setMinimized((v) => !v)}
        onHandlePointerDown={drag.onPointerDown}
      />
    </div>
  );
}

interface BubbleProps {
  state: WebphoneState;
  inCall: boolean;
  muted: boolean;
  pos: { bottom: number; right: number };
  onClick: () => void;
  /** A cada movimento (ja preso a viewport pelo tamanho da bolha). */
  onPosChange: (next: { bottom: number; right: number }) => void;
  /** Ao soltar — e so aqui que a posicao vai pro localStorage. */
  onCommitPos: (next: { bottom: number; right: number }) => void;
}

function WebphoneBubble({
  state,
  inCall,
  muted,
  pos,
  onClick,
  onPosChange,
  onCommitPos,
}: BubbleProps) {
  // Arrasto pelo hook compartilhado (`use-drag-position.ts`) — a MESMA mecanica
  // do painel e do cartao. A copia que morava aqui dentro tinha um defeito de
  // ciclo de vida (listeners removidos no primeiro re-render do pai, ou seja, no
  // primeiro movimento): a bolha andava uns poucos px e travava. Detalhe no
  // docblock do hook.
  const drag = useDragPosition({
    pos,
    size: { width: BUBBLE_SIZE, height: BUBBLE_SIZE },
    onChange: onPosChange,
    onCommit: onCommitPos,
  });

  // A bolha e ela mesma o alvo do arrasto E o botao que abre o painel: sem este
  // guard, largar a bolha noutro canto abriria o painel junto.
  const handleClick = () => {
    if (drag.wasDragged()) return;
    onClick();
  };

  const dotColor =
    state === 'registered'
      ? 'bg-emerald-500'
      : state === 'connecting'
        ? 'bg-amber-500 animate-pulse'
        : state === 'failed' || state === 'disconnected'
          ? 'bg-zinc-400'
          : 'bg-blue-500 animate-pulse';

  const Icon =
    state === 'disconnected' || state === 'failed'
      ? WifiOff
      : state === 'connecting'
        ? Loader2
        : inCall
          ? muted
            ? MicOff
            : PhoneCall
          : Phone;

  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={drag.onPointerDown}
      // touch-action: none impede o browser de tomar conta do gesto pra
      // scroll/zoom no mobile — sem isso o pointer events não disparam direito.
      style={{ touchAction: 'none' }}
      className={cn(
        'group relative flex h-14 w-14 items-center justify-center rounded-full shadow-lg',
        'border-2 transition-all hover:scale-105 active:scale-95',
        'cursor-grab active:cursor-grabbing select-none',
        inCall
          ? 'bg-primary text-primary-foreground border-primary/50 shadow-primary/30 animate-pulse'
          : state === 'registered'
            ? 'bg-card text-foreground border-border'
            : 'bg-muted text-muted-foreground border-border',
      )}
      aria-label="Webphone (arraste pra mover)"
      title={`Webphone — ${state} (arraste pra mover)`}
    >
      <Icon className={cn('h-6 w-6', state === 'connecting' && 'animate-spin')} />
      <span
        className={cn(
          'absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-background',
          dotColor,
        )}
      />
    </button>
  );
}
